/**
 * API Service - Production Ready
 * Centralized API communication with proper error handling
 */

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'cs_token';
const REFRESH_TOKEN_KEY = 'cs_refresh';
const USER_KEY = 'cs_user';

// ==================== Token Management ====================
// Using sessionStorage for tokens (more secure than localStorage)
// Tokens are cleared when browser/tab is closed

const tokenManager = {
  getToken: () => sessionStorage.getItem(TOKEN_KEY),
  setToken: (token) => sessionStorage.setItem(TOKEN_KEY, token),
  removeToken: () => sessionStorage.removeItem(TOKEN_KEY),

  getRefreshToken: () => sessionStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token) => sessionStorage.setItem(REFRESH_TOKEN_KEY, token),
  removeRefreshToken: () => sessionStorage.removeItem(REFRESH_TOKEN_KEY),

  // User data (non-sensitive)
  getUser: () => {
    try {
      const data = sessionStorage.getItem(USER_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },
  setUser: (user) => {
    // Only store non-sensitive user info
    const safeUser = {
      id: user.id || user._id,
      fullName: user.fullName,
      email: user.email,
      mobile: user.mobile,
      roleName: user.roleName,
      companyName: user.companyName,
      designation: user.designation,
      isPhoneVerified: user.isPhoneVerified,
    };
    sessionStorage.setItem(USER_KEY, JSON.stringify(safeUser));
  },
  removeUser: () => sessionStorage.removeItem(USER_KEY),

  clearAll: () => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  },

  isAuthenticated: () => !!sessionStorage.getItem(TOKEN_KEY),
};

// ==================== API Client ====================

class APIError extends Error {
  constructor(message, code, status, details = null, originalData = null) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.originalData = originalData; // Store the full API response
  }
}

const getHeaders = (includeAuth = true) => {
  const headers = { 'Content-Type': 'application/json' };

  if (includeAuth) {
    const token = tokenManager.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

// Helper function to trigger logout with error message
const triggerLogout = (errorMessage, errorCode) => {
  if (!tokenManager.getToken()) return; // Already logged out

  const logoutMessage = errorMessage || 'Your session has expired. Please login again.';

  // Clear auth first
  tokenManager.clearAll();

  // Dispatch logout event with error details immediately
  // AuthContext will handle showing toast and redirecting
  const event = new CustomEvent('auth:logout', {
    detail: {
      message: logoutMessage,
      code: errorCode || 'SESSION_EXPIRED'
    },
    bubbles: true,
    cancelable: false
  });

  // Dispatch synchronously to ensure it's handled
  window.dispatchEvent(event);
};

const handleResponse = async (response) => {
  let data = {};
  const statusCode = response.status;

  try {
    const text = await response.text();
    if (text) {
      data = JSON.parse(text);
    }
  } catch (e) {
    console.log(e)
    // If response is not JSON, create a basic error object
    data = { message: response.statusText || 'Request failed' };
  }

  // Check for errors in response (even if response.ok is true)
  // Some APIs return success: false in the body even with 200 status
  if (!response.ok || data.success === false) {
    // Error codes that should trigger logout
    // Add more codes here as needed, or set shouldLogout: true in API response
    const logoutErrorCodes = [
      'SESSION_EXPIRED',
      'TOKEN_EXPIRED',
      'INVALID_TOKEN',
      'UNAUTHORIZED',
      'ACCOUNT_SUSPENDED',
      'ACCOUNT_DISABLED',
      'ACCOUNT_DELETED',
      'FORBIDDEN_ACCESS',
      'UNAUTHORIZED_ACCESS',
      'RATE_LIMIT_EXCEEDED'
    ];

    const errorCode = data.code || (statusCode === 401 ? 'UNAUTHORIZED' : statusCode === 403 ? 'FORBIDDEN' : 'ERROR');
    // Prioritize the 'error' field for more specific error messages, fallback to 'message'.
    // In development the backend puts the full error OBJECT in `error` (sendErrorDev),
    // so only use it when it's actually a string.
    const errorMessage = (typeof data.error === 'string' && data.error) ||
      (typeof data.message === 'string' && data.message) ||
      (statusCode === 401 ? 'Unauthorized. Please login again.' : statusCode === 403 ? 'Access forbidden.' : 'Request failed');

    // Check if this error should trigger logout
    // You can trigger logout by:
    // 1. Including error code in logoutErrorCodes array above
    // 2. Returning HTTP status 401 or 403
    // 3. Setting shouldLogout: true in API response
    const shouldLogout = logoutErrorCodes.includes(errorCode) ||
      statusCode === 401 ||
      statusCode === 403 ||
      (data.shouldLogout === true);

    if (shouldLogout) {
      triggerLogout(errorMessage, errorCode);
    }
    console.log(data,errorMessage) 

    throw new APIError(
      errorMessage,
      errorCode,
      statusCode,
      data.details || data.errors,
      data // Pass the full response data
    );
  }

  return data;
};

const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    method: options.method || 'GET',
    headers: getHeaders(options.includeAuth !== false),
    ...options,
  };

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body);
  } else if (options.body) {
    config.body = options.body;
  }

  try {
    const response = await fetch(url, config);

    // Handle 401 - try token refresh
    if (response.status === 401 && tokenManager.getToken() && !options.skipRefresh) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        config.headers.Authorization = `Bearer ${tokenManager.getToken()}`;
        const retryResponse = await fetch(url, config);
        return handleResponse(retryResponse);
      }
      // Refresh failed - let handleResponse handle the logout with proper error message
      // Continue to handleResponse to parse the error and trigger logout
      // Don't return early - let it go through handleResponse
    }

    // Always go through handleResponse to check for logout conditions
    return handleResponse(response);
  } catch (error) {
    // If it's an APIError that should trigger logout, it's already handled
    if (error instanceof APIError) {
      // Check if this error should have triggered logout
      if ((error.status === 401) && tokenManager.getToken()) {
        // Logout might not have been triggered, trigger it now
        triggerLogout(error.message, error.code);
      }
      throw error;
    }
    throw new APIError(
      error.message || 'Network error. Please check your connection.',
      'NETWORK_ERROR',
      0
    );
  }
};

// Token refresh helper
const refreshAccessToken = async () => {
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      tokenManager.setToken(data.data.accessToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// ==================== Auth API ====================

export const authAPI = {
  /**
   * Register new user
   */
  register: async (userData) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: userData,
      includeAuth: false,
    });
    return response;
  },

  /**
   * Login - sends OTP to mobile
   */
  login: async (mobile) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: { mobile },
      includeAuth: false,
    });
    return response;
  },

  /**
   * Verify OTP (unified for both login and registration)
   */
  verifyOTP: async (mobile, otp) => {
    const response = await apiRequest('/auth/verify-otp', {
      method: 'POST',
      body: { mobile, otp },
      includeAuth: false,
    });

    if (response.success && response.data) {
      // Store tokens and user
      if (response.data.tokens) {
        tokenManager.setToken(response.data.tokens.accessToken);
        tokenManager.setRefreshToken(response.data.tokens.refreshToken);
      }
      if (response.data.user) {
        tokenManager.setUser(response.data.user);
      }
    }

    return response;
  },

  /**
   * Resend OTP
   */
  resendOTP: async (mobile) => {
    return apiRequest('/auth/resend-otp', {
      method: 'POST',
      body: { mobile },
      includeAuth: false,
    });
  },

  /**
   * Get current user profile
   */
  getProfile: async () => {
    const response = await apiRequest('/auth/profile');
    if (response.success && response.data?.user) {
      tokenManager.setUser(response.data.user);
    }
    return response;
  },

  /**
   * Update profile
   */
  updateProfile: async (data) => {
    const response = await apiRequest('/auth/profile', {
      method: 'PUT',
      body: data,
    });
    if (response.success && response.data?.user) {
      tokenManager.setUser(response.data.user);
    }
    return response;
  },

  /**
   * Logout
   */
  logout: async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch {
      // Ignore errors
    } finally {
      tokenManager.clearAll();
    }
  },

  /**
   * Logout from all devices
   */
  logoutAll: async () => {
    try {
      await apiRequest('/auth/logout-all', { method: 'POST' });
    } catch {
      // Ignore errors
    } finally {
      tokenManager.clearAll();
    }
  },
};

// ==================== Services API ====================

export const servicesAPI = {
  // Public endpoints
  getAllServices: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/services${query ? `?${query}` : ''}`, { includeAuth: false });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/services${query ? `?${query}` : ''}`, { includeAuth: false });
  },

  getBySlug: async (slug) => {
    return apiRequest(`/services/${slug}`, { includeAuth: false });
  },

  getServiceBySlug: async (slug) => {
    return apiRequest(`/services/${slug}`, { includeAuth: false });
  },

  getCategories: async () => {
    return apiRequest('/services/categories', { includeAuth: false });
  },

  getServiceCategories: async () => {
    return apiRequest('/services/categories', { includeAuth: false });
  },

  // Download raw/empty template for a service
  downloadRawTemplate: async (slug) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/services/${slug}/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || 'Failed to download template',
        error.code || 'DOWNLOAD_ERROR',
        response.status
      );
    }
    return response.blob();
  },

  // Admin
  createService: async (data) => apiRequest('/services', { method: 'POST', body: data }),
  create: async (data) => apiRequest('/services', { method: 'POST', body: data }),
  updateService: async (id, data) => apiRequest(`/services/${id}`, { method: 'PUT', body: data }),
  update: async (id, data) => apiRequest(`/services/${id}`, { method: 'PUT', body: data }),
  deleteService: async (id) => apiRequest(`/services/${id}`, { method: 'DELETE' }),
  delete: async (id) => apiRequest(`/services/${id}`, { method: 'DELETE' }),
};

// ==================== Cart API ====================

export const cartAPI = {
  getCart: async () => apiRequest('/cart'),

  addToCart: async (serviceId, quantity = 1) => {
    return apiRequest('/cart/add', {
      method: 'POST',
      body: { serviceId, quantity },
    });
  },

  updateCartItem: async (serviceId, quantity) => {
    return apiRequest(`/cart/item/${serviceId}`, {
      method: 'PUT',
      body: { quantity },
    });
  },

  removeFromCart: async (serviceId) => {
    return apiRequest(`/cart/item/${serviceId}`, { method: 'DELETE' });
  },

  clearCart: async () => {
    return apiRequest('/cart/clear', { method: 'DELETE' });
  },
};

// ==================== Orders API ====================

export const ordersAPI = {
  create: async (paymentMethod) => {
    return apiRequest('/orders', {
      method: 'POST',
      body: { paymentMethod },
    });
  },

  createOrder: async (paymentMethod) => {
    return apiRequest('/orders', {
      method: 'POST',
      body: { paymentMethod },
    });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders${query ? `?${query}` : ''}`);
  },

  getUserOrders: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders${query ? `?${query}` : ''}`);
  },

  getById: async (id) => apiRequest(`/orders/${id}`),
  getOrderById: async (id) => apiRequest(`/orders/${id}`),

  cancel: async (id) => {
    return apiRequest(`/orders/${id}/cancel`, { method: 'PUT' });
  },

  cancelOrder: async (id) => {
    return apiRequest(`/orders/${id}/cancel`, { method: 'PUT' });
  },

  // Admin
  getAllAdmin: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders/admin/all${query ? `?${query}` : ''}`);
  },

  updateStatus: async (id, statusData) => {
    return apiRequest(`/orders/${id}/status`, {
      method: 'PUT',
      body: statusData,
    });
  },
};

// ==================== Documents API ====================

export const documentsAPI = {
  upload: async (formData) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return response.json();
  },

  uploadDocument: async (formData) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return response.json();
  },

  getForOrder: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}`);
  },

  getOrderDocuments: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}`);
  },

  getDocumentStatus: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}/status`);
  },

  submitOrder: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}/submit`, { method: 'POST' });
  },

  checkOrderComplete: async (orderId) => {
    return apiRequest(`/documents/check-order-complete/${orderId}`, { method: 'POST' });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/documents${query ? `?${query}` : ''}`);
  },

  getUserDocuments: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/documents${query ? `?${query}` : ''}`);
  },

  getById: async (id) => apiRequest(`/documents/${id}`),

  delete: async (id) => {
    return apiRequest(`/documents/${id}`, { method: 'DELETE' });
  },

  deleteDocument: async (id) => {
    return apiRequest(`/documents/${id}`, { method: 'DELETE' });
  },

  // Admin
  process: async (id, action, data = {}) => {
    return apiRequest(`/documents/${id}/process`, {
      method: 'PUT',
      body: { action, ...data },
    });
  },

  download: async (id) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new APIError('Download failed', 'DOWNLOAD_ERROR', response.status);
    return response.blob();
  },

  downloadDocument: async (id) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new APIError('Download failed', 'DOWNLOAD_ERROR', response.status);
    return response.blob();
  },
};

// ==================== Meetings API ====================

export const meetingsAPI = {
  getTemplate: async () => {
    const response = await fetch(`${API_BASE_URL}/meetings/minutes/template`, {
      headers: getHeaders(),
    });
    if (!response.ok) throw new APIError('Failed to download template', 'DOWNLOAD_ERROR', response.status);
    return response.blob();
  },

  generateMinutes: async (file, format = 'docx') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);

    const response = await fetch(`${API_BASE_URL}/meetings/minutes/generate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(error.message || 'Generation failed', error.code || 'ERROR', response.status);
    }

    return response.blob();
  },

  previewData: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/meetings/minutes/preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
      body: formData,
    });

    return handleResponse(response);
  },
};

// ==================== AI Models API ====================

export const aiModelsAPI = {
  // Get all models (admin only)
  getAll: async () => {
    return apiRequest('/ai-models');
  },

  // Get model by ID
  getById: async (id) => {
    return apiRequest(`/ai-models/${id}`);
  },

  // Create new model
  create: async (modelData) => {
    return apiRequest('/ai-models', {
      method: 'POST',
      body: modelData,
    });
  },

  // Update model
  update: async (id, modelData) => {
    return apiRequest(`/ai-models/${id}`, {
      method: 'PUT',
      body: modelData,
    });
  },

  // Delete model
  delete: async (id) => {
    return apiRequest(`/ai-models/${id}`, { method: 'DELETE' });
  },

  // Test model
  test: async (id) => {
    return apiRequest(`/ai-models/${id}/test`, { method: 'POST' });
  },

  // Set as default
  setDefault: async (id) => {
    return apiRequest(`/ai-models/${id}/set-default`, { method: 'POST' });
  },

  // Get provider options
  getProviders: async () => {
    return apiRequest('/ai-models/providers');
  },

  // Get default model
  getDefault: async () => {
    return apiRequest('/ai-models/default');
  },

  // Get usage logs
  getUsageLogs: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/ai-models/usage/logs${query ? `?${query}` : ''}`);
  },

  // Get usage summary
  getUsageSummary: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/ai-models/usage/summary${query ? `?${query}` : ''}`);
  },

  // Get usage for an order
  getOrderUsage: async (orderId) => {
    return apiRequest(`/ai-models/usage/order/${orderId}`);
  },
};

// ==================== Company Profile API ====================

export const companyProfileAPI = {
  // Get user's company profile
  getProfile: async () => {
    return apiRequest('/company-profile');
  },

  // Get service-specific profile
  getServiceProfile: async (serviceType) => {
    return apiRequest(`/company-profile/service/${serviceType}`);
  },

  // Download pre-populated template
  downloadTemplate: async (serviceType = 'board_meeting', meetingNumber = null) => {
    const token = tokenManager.getToken();
    const params = new URLSearchParams({ serviceType });
    if (meetingNumber) params.append('meetingNumber', meetingNumber);

    const response = await fetch(`${API_BASE_URL}/company-profile/template/prepopulated?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || 'Failed to download template',
        error.code || 'DOWNLOAD_ERROR',
        response.status
      );
    }

    return response.blob();
  },
};

// ==================== Users API (Admin) ====================

export const usersAPI = {
  // Get all users (admin only)
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/auth/admin/users${query ? `?${query}` : ''}`);
  },

  // Get user by ID
  getById: async (id) => {
    return apiRequest(`/auth/admin/users/${id}`);
  },

  // Update user
  update: async (id, userData) => {
    return apiRequest(`/auth/admin/users/${id}`, {
      method: 'PUT',
      body: userData,
    });
  },

  // Delete user
  delete: async (id) => {
    return apiRequest(`/auth/admin/users/${id}`, { method: 'DELETE' });
  },

  // Toggle user status
  toggleStatus: async (id) => {
    return apiRequest(`/auth/admin/users/${id}/toggle-status`, { method: 'PUT' });
  },

  // Change user role
  changeRole: async (id, roleId) => {
    return apiRequest(`/auth/admin/users/${id}/role`, {
      method: 'PUT',
      body: { roleId },
    });
  },
  // Lock user account
  lock: async (id, reason, lockDuration) => {
    return apiRequest(`/auth/admin/users/${id}/lock`, {
      method: 'POST',
      body: { reason, lockDuration },
    });
  },

  // Unlock user account
  unlock: async (id, reason) => {
    return apiRequest(`/auth/admin/users/${id}/unlock`, {
      method: 'POST',
      body: { reason },
    });
  },
};

// ==================== Roles API ====================

export const rolesAPI = {
  // Get all roles
  getAll: async () => {
    return apiRequest('/roles');
  },

  // Get role by ID
  getById: async (id) => {
    return apiRequest(`/roles/${id}`);
  },

  // Create role
  create: async (roleData) => {
    return apiRequest('/roles', {
      method: 'POST',
      body: roleData,
    });
  },

  // Update role
  update: async (id, roleData) => {
    return apiRequest(`/roles/${id}`, {
      method: 'PUT',
      body: roleData,
    });
  },

  // Delete role
  delete: async (id) => {
    return apiRequest(`/roles/${id}`, { method: 'DELETE' });
  },

  // Get all permissions
  getPermissions: async () => {
    return apiRequest('/roles/permissions');
  },
};

// ==================== Health Check ====================

export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
    return response.json();
  } catch {
    return { success: false, status: 'unavailable' };
  }
};

// ==================== Professional Assignment API ====================

export const professionalAssignmentAPI = {
  // Get my assignments (for corporate users)
  getMyAssignments: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/professional-assignments/my-assignments${query ? `?${query}` : ''}`);
  },

  // Get professional assignments (for professional users)
  getMyProfessionalAssignments: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/professional-assignments/my-professional-assignments${query ? `?${query}` : ''}`);
  },

  // Get available professionals for assignment
  getAvailableProfessionals: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/professional-assignments/available-professionals${query ? `?${query}` : ''}`);
  },

  // Create new assignment
  createAssignment: async (assignmentData) => {
    return apiRequest('/professional-assignments', {
      method: 'POST',
      body: assignmentData,
    });
  },

  // Update assignment
  updateAssignment: async (assignmentId, updateData) => {
    return apiRequest(`/professional-assignments/${assignmentId}`, {
      method: 'PUT',
      body: updateData,
    });
  },

  // Delete assignment
  deleteAssignment: async (assignmentId) => {
    return apiRequest(`/professional-assignments/${assignmentId}`, {
      method: 'DELETE',
    });
  },

  // Get revoked/inactive assignments
  getRevokedAccess: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/professional-assignments/revoked-access${queryString ? `?${queryString}` : ''}`);
  },
};

// ==================== Drafts API ====================

export const draftsAPI = {
  // Get all drafts
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/drafts${query ? `?${query}` : ''}`);
  },

  // Get draft by ID
  getById: async (id) => {
    return apiRequest(`/drafts/${id}`);
  },

  // Update draft
  updateDraft: async (draftId, updateData) => {
    return apiRequest(`/drafts/${draftId}`, {
      method: 'PUT',
      body: updateData,
    });
  },

  // Delete draft
  deleteDraft: async (draftId) => {
    return apiRequest(`/drafts/${draftId}`, {
      method: 'DELETE',
    });
  },
};

// ==================== Companies API ====================

export const companiesAPI = {
  // Get all companies
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/companies${query ? `?${query}` : ''}`);
  },

  // Get company by ID
  getById: async (id) => {
    return apiRequest(`/companies/${id}`);
  },


};

// ==================== Exports ====================

export { tokenManager, APIError };
export const isAuthenticated = () => tokenManager.isAuthenticated();
export const getCurrentUser = () => tokenManager.getUser();
export const logout = () => {
  tokenManager.clearAll();
  window.location.href = '/login';
};

// Legacy exports for backward compatibility
export const setAuthToken = tokenManager.setToken;
export const getAuthToken = tokenManager.getToken;
export const removeAuthToken = tokenManager.removeToken;
export const setCurrentUser = tokenManager.setUser;
export const removeCurrentUser = tokenManager.removeUser;
